/**
 * SiteGlue Human Collaboration terminology — customer-facing display labels.
 *
 * Hierarchy:
 *   Human-in-the-Loop™
 *   ├── Team Handoff™
 *   ├── Team Takeover™
 *   └── Phone Handling Rules
 *
 * Keep internal enums / MCP tool names / API contracts unchanged.
 * Map them to these labels in UI and marketing only.
 */

export const HITL_TERMS = {
  hitl: "Human-in-the-Loop™",
  hitlAlways: "Always a Human-in-the-Loop™",
  teamHandoff: "Team Handoff™",
  teamTakeover: "Team Takeover™",
  phoneRules: "Phone Handling Rules",
  humanApproval: "Human Approval",
  humanCollaboration: "Human Collaboration",
  teamHandoffWorkflows: "Team Handoff Workflows",
  teamHandoffInstructions: "Team Handoff Instructions",
  teamHandoffAssistant: "Team Handoff Assistant",
} as const;

/** Internal identifiers → display labels (do not rename runtime keys). */
export const HITL_DISPLAY_LABELS = {
  human_handoff: HITL_TERMS.teamHandoff,
  humanHandoff: HITL_TERMS.teamHandoff,
  "Human Handoff": HITL_TERMS.teamHandoff,
  team_member_takeover: HITL_TERMS.teamTakeover,
  teamMemberTakeover: HITL_TERMS.teamTakeover,
  "Team Member Takeover": HITL_TERMS.teamTakeover,
  "Human Takeover": HITL_TERMS.teamTakeover,
  human_in_the_loop: HITL_TERMS.hitl,
} as const;

export const HITL_DEFINITIONS = {
  hitl:
    "SiteGlue’s philosophy and collaboration layer. AI handles routine work while people remain responsible for judgment, exceptions, approvals, and important decisions.",
  teamHandoff:
    "The AI Employee recognizes that a conversation should involve a team member, collects the required context, and routes or transfers the interaction according to approved rules.",
  teamTakeover:
    "A team member joins or continues an AI-led conversation while preserving available context and pausing or disabling the AI where appropriate.",
  phoneRules:
    "Channel-specific policies that determine how calls are handled based on industry, caller intent, caller type, business hours, local time, location, department, urgency, scheduling availability, team-member availability, primary action, fallback action, transfer rules, whisper messages, and data-collection requirements.",
  humanApproval:
    "Training, workflow, knowledge, integration, and production changes require explicit human review and approval.",
} as const;

export const HITL_PUBLIC_ROUTES = {
  hitl: "/human-in-the-loop",
  teamHandoff: "/team-handoff",
  teamTakeover: "/team-takeover",
  phoneRules: "/phone-handling-rules",
} as const;

export const HITL_LEGACY_NOTE =
  "Team Handoff™ was previously described as Human Handoff. Team Takeover™ was previously described as Team Member Takeover.";

/** Rewrite customer-facing HITL collaboration labels in marketing copy. */
export function applyHitlDisplayTerminology(input: string): string {
  return input
    .replace(/Team Handoff Actions \(MPC\)/g, HITL_TERMS.teamHandoffWorkflows)
    .replace(/Team Handoff Actions \(MCP\)/g, HITL_TERMS.teamHandoffWorkflows)
    .replace(/Team Handoff Delegate \(MPC\)/g, HITL_TERMS.teamHandoffAssistant)
    .replace(/View Team Handoff Agent Prompt/g, `View ${HITL_TERMS.teamHandoffInstructions}`)
    .replace(/Team Member Takeover/g, HITL_TERMS.teamTakeover)
    .replace(/Human Takeover/g, HITL_TERMS.teamTakeover)
    .replace(/Human Handoff/g, HITL_TERMS.teamHandoff);
}
